<script setup>
/**
 * @fileoverview ConsoleSessionTabs — horizontal or stacked session pills.
 *
 * Shared by ConsoleToolbar (inline strip) and ConsoleSessionDock (floating).
 * Uses the consoleSessions store; renders nothing when there are no sessions.
 */
import { computed, onMounted, onBeforeUnmount, ref } from 'vue'
import { Monitor, X } from 'lucide-vue-next'
import {
  useConsoleSessionsStore,
  IDLE_WARN_BEFORE_MS
} from '@/stores/consoleSessions'

const props = defineProps({
  /** `toolbar`: single scrollable row in the console header. `dock`: stacked in the floating dock. */
  variant: {
    type: String,
    default: 'toolbar',
    validator: v => ['toolbar', 'dock'].includes(v)
  }
})

const sessionsStore = useConsoleSessionsStore()

const sessions = computed(() => sessionsStore.sessionList)
const activeVmId = computed(() => sessionsStore.activeVmId)

const now = ref(Date.now())
let tickerId = null

onMounted(() => {
  tickerId = setInterval(() => {
    now.value = Date.now()
  }, 1000)
})

onBeforeUnmount(() => {
  if (tickerId) clearInterval(tickerId)
})

function isMinimized(session) {
  return session.idleDeadline !== null && session.idleDeadline !== undefined
}

function remainingMs(session) {
  if (!isMinimized(session)) return null
  return Math.max(0, session.idleDeadline - now.value)
}

function shouldShowCountdown(session) {
  const remaining = remainingMs(session)
  return remaining !== null && remaining <= IDLE_WARN_BEFORE_MS
}

function formatCountdown(session) {
  const remaining = remainingMs(session)
  if (remaining === null) return ''
  const totalSeconds = Math.floor(remaining / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

function stateDotClass(session) {
  const state = session.controller.connectionState.value
  switch (state) {
    case 'connected':
      return 'bg-emerald-500'
    case 'connecting':
    case 'reconnecting':
      return 'bg-amber-500 animate-pulse'
    case 'error':
      return 'bg-red-500'
    default:
      return 'bg-gray-400 dark:bg-gray-500'
  }
}

function handleActivateTab(session) {
  sessionsStore.focusSession(session.sessionKey || session.vmId)
}

function handleClose(session, event) {
  event.stopPropagation()
  sessionsStore.closeSession(session.sessionKey || session.vmId)
}

const listClass = computed(() =>
  props.variant === 'toolbar'
    ? 'console-session-tabs console-session-tabs--toolbar'
    : 'console-session-tabs console-session-tabs--dock'
)
</script>

<template>
  <div
    v-if="sessions.length > 0"
    :class="listClass"
    role="tablist"
    aria-label="Console sessions"
  >
    <div
      v-for="session in sessions"
      :key="session.sessionKey"
      role="tab"
      tabindex="0"
      :aria-selected="(session.sessionKey || session.vmId) === activeVmId"
      :class="[
        'session-tab',
        (session.sessionKey || session.vmId) === activeVmId ? 'is-active' : '',
        isMinimized(session) ? 'is-minimized' : ''
      ]"
      @click="handleActivateTab(session)"
      @keydown.enter.prevent="handleActivateTab(session)"
      @keydown.space.prevent="handleActivateTab(session)"
    >
      <div class="session-tab-main">
        <Monitor class="h-4 w-4 shrink-0 text-gray-500 dark:text-slate-400" aria-hidden="true" />
        <span :class="['state-dot h-2 w-2 shrink-0 rounded-full', stateDotClass(session)]" />
        <span class="tab-label min-w-0">
          <span class="vm-name">{{ session.vmName || session.vmId }}</span>
          <span v-if="session.labName" class="lab-name">{{ session.labName }}</span>
        </span>
        <span
          v-if="shouldShowCountdown(session)"
          class="countdown"
          :title="`Closes in ${formatCountdown(session)}`"
          aria-label="Idle countdown"
        >
          {{ formatCountdown(session) }}
        </span>
      </div>
      <button
        type="button"
        class="close-btn"
        :aria-label="`Close ${session.vmName || session.vmId} session`"
        @click="handleClose(session, $event)"
      >
        <X class="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.console-session-tabs {
  display: flex;
  gap: 0.375rem;
  min-width: 0;
}

.console-session-tabs--toolbar {
  flex-direction: row;
  flex: 1 1 auto;
  max-width: min(100%, 36rem);
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 0.125rem;
  scrollbar-width: thin;
}

.console-session-tabs--dock {
  flex-direction: column;
  width: 100%;
  align-items: stretch;
}

.session-tab {
  display: flex;
  align-items: stretch;
  flex-shrink: 0;
  border-radius: 0.5rem;
  border: 1px solid rgb(229 231 235);
  background: rgb(255 255 255);
  color: rgb(17 24 39);
  font-size: 0.8125rem;
  cursor: pointer;
  text-align: left;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
}

.dark .session-tab {
  border-color: rgb(71 85 105);
  background: rgb(30 41 59);
  color: rgb(226 232 240);
}

.session-tab:hover {
  background: rgb(249 250 251);
}

.dark .session-tab:hover {
  background: rgb(51 65 85);
}

.session-tab.is-active {
  border-color: rgb(99 102 241);
  box-shadow: 0 0 0 1px rgb(99 102 241);
}

.session-tab.is-minimized {
  opacity: 0.9;
}

.session-tab-main {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
  padding: 0.375rem 0.25rem 0.375rem 0.5rem;
  flex: 1 1 auto;
}

.console-session-tabs--dock .session-tab {
  width: 100%;
}

.tab-label {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.vm-name {
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 11rem;
}

.lab-name {
  font-size: 0.65rem;
  color: rgb(107 114 128);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dark .lab-name {
  color: rgb(148 163 184);
}

.countdown {
  font-variant-numeric: tabular-nums;
  font-size: 0.65rem;
  color: rgb(180 83 9);
  background: rgb(254 243 199);
  border-radius: 0.25rem;
  padding: 0.125rem 0.35rem;
  margin-left: 0.125rem;
}

.dark .countdown {
  color: rgb(252 211 77);
  background: rgba(120, 53, 15, 0.45);
}

.close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  padding: 0 0.45rem;
  border: none;
  border-left: 1px solid rgb(229 231 235);
  border-radius: 0 0.5rem 0.5rem 0;
  background: transparent;
  color: rgb(107 114 128);
  cursor: pointer;
}

.dark .close-btn {
  border-left-color: rgb(71 85 105);
  color: rgb(148 163 184);
}

.close-btn:hover {
  background: rgb(254 226 226);
  color: rgb(220 38 38);
}

.dark .close-btn:hover {
  background: rgba(248, 113, 113, 0.15);
  color: rgb(248 113 113);
}
</style>
